import { useEffect, useMemo, useState } from 'react';
import type { ProjectDoc, WbsItemDoc } from '@mct/shared';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input, Label } from '../ui/Input';

export interface CreateAndLinkSubmit {
  name: string;
  phaseId: string;
  parentTaskId: string | null;
  assigneeId: string | null;
}

/**
 * Compose a new WBS task from a suggestion and link it in one step.
 *
 * UI flow:
 *   1. Task name (prefilled from `defaultName`, freely editable).
 *   2. Phase (required) — only `kind === 'phase'` rows.
 *   3. Parent task (optional) — `kind === 'task'` rows under the chosen
 *      phase. Disabled until a phase is picked.
 *   4. Assignee (optional).
 *
 * Submit hands the value back to the parent; the parent owns the
 * createWbsItem → acceptNewWbsItem sequence because the suggestion
 * snapshot lives on the parent's side of the data flow.
 */
export function CreateAndLinkDialog({
  open,
  onClose,
  defaultName,
  wbsItems,
  assignees,
  busy,
  onSubmit,
  project: _project,
}: {
  open: boolean;
  onClose: () => void;
  defaultName: string;
  wbsItems: readonly WbsItemDoc[];
  assignees: ReadonlyArray<{ assigneeId: string; name: string }>;
  busy: boolean;
  onSubmit: (input: CreateAndLinkSubmit) => Promise<void>;
  project: ProjectDoc | null;
}) {
  const [name, setName] = useState(defaultName);
  const [phaseId, setPhaseId] = useState('');
  const [parentTaskId, setParentTaskId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  // Reset internal state whenever the dialog is (re)opened so a previous
  // selection doesn't leak into a brand-new suggestion.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setPhaseId('');
      setParentTaskId('');
      setAssigneeId('');
    }
  }, [open, defaultName]);

  const phases = useMemo(
    () =>
      wbsItems
        .filter((w) => w.kind === 'phase')
        .slice()
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
    [wbsItems],
  );

  // Tasks reachable under the currently selected phase. A task may sit
  // directly under the phase or nest one level deeper — both should be
  // valid parent choices.
  const parentTaskOptions = useMemo(() => {
    if (!phaseId) return [] as WbsItemDoc[];
    const childrenByParent = new Map<string | null, WbsItemDoc[]>();
    for (const w of wbsItems) {
      const key = w.parentItemId ?? null;
      const bucket = childrenByParent.get(key);
      if (bucket) bucket.push(w);
      else childrenByParent.set(key, [w]);
    }
    const out: WbsItemDoc[] = [];
    const visit = (parentId: string) => {
      const kids = childrenByParent.get(parentId) ?? [];
      for (const k of kids) {
        if (k.kind === 'task') {
          out.push(k);
          visit(k.itemId);
        }
      }
    };
    visit(phaseId);
    return out.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [wbsItems, phaseId]);

  const canSubmit = name.trim().length > 0 && phaseId.length > 0 && !busy;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="新規 WBS タスクを作成して紐付け"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            キャンセル
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                phaseId,
                parentTaskId: parentTaskId || null,
                assigneeId: assigneeId || null,
              })
            }
          >
            作成して紐付け
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>タスク名</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="create-link-name"
          />
        </div>
        <div>
          <Label>フェーズ</Label>
          <select
            className="input"
            value={phaseId}
            onChange={(e) => {
              setPhaseId(e.target.value);
              setParentTaskId('');
            }}
            data-testid="create-link-phase"
          >
            <option value="">— フェーズを選択 —</option>
            {phases.map((p) => (
              <option key={p.itemId} value={p.itemId}>
                {p.sortKey} {p.name}
              </option>
            ))}
          </select>
          {phases.length === 0 ? (
            <p className="mt-1 text-xs text-amber-700">
              フェーズがまだありません。先に WBS でフェーズを作成してください。
            </p>
          ) : null}
        </div>
        <div>
          <Label>親課題 (任意)</Label>
          <select
            className="input"
            value={parentTaskId}
            onChange={(e) => setParentTaskId(e.target.value)}
            disabled={!phaseId}
            data-testid="create-link-parent"
          >
            <option value="">— フェーズ直下 —</option>
            {parentTaskOptions.map((t) => (
              <option key={t.itemId} value={t.itemId}>
                {t.sortKey} {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>担当者 (任意)</Label>
          <select
            className="input"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            data-testid="create-link-assignee"
          >
            <option value="">— 未割当 —</option>
            {assignees.map((a) => (
              <option key={a.assigneeId} value={a.assigneeId}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Dialog>
  );
}
